import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import {CheckpointResult} from "../../interfaces/control/Checkpoint";
import {CheckpointBlock} from "../../interfaces/control/CheckpointBlock";
import {CheckpointExecute} from "../../interfaces/control/CheckpointExecute";
import {UploadFile} from "antd/es/upload";
import apiControl from "../../service/api/ApiControl";
import ToastService from "../../service/ToastService";
import {isErrorAlreadyNotified} from "../../service/AuthenticatedApi";
import i18next from "i18next";

interface CheckpointsBlockState {
  data: CheckpointBlock[];
  unprocessedMercureMessages: CheckpointResult[];
}

const initialState: CheckpointsBlockState = {
  data: [],
  unprocessedMercureMessages: [],
};

const updateStateCheckpointResult = (state: CheckpointsBlockState, checkpointResult: CheckpointResult): void => {
  state.data.forEach((checkpointBlock: CheckpointBlock) => {
    checkpointBlock.checkpoints.forEach((checkpoint) => {
      if (checkpoint.result) {
        if (checkpoint.result.id === checkpointResult.id) {
          checkpoint.result = checkpointResult;
        }
      }
    });
  });
};

export const fetchCheckpointsBlock = createAsyncThunk(
  "checkpointBlock/fetchCheckpointsBlock",
  async (plantId: number) => {
    return apiControl.checkpointsBlocks(plantId);
  },
);

export const fetchCheckpointsExecuteAll = createAsyncThunk(
  "checkpointBlock/fetchCheckpointsExecuteAll",
  async (plantId: number) => {
    try {
      return await apiControl.checkpointsExecuteAll(plantId);
    } catch (error) {
      if (!isErrorAlreadyNotified(error)) {
        ToastService.error(i18next.t("fleet:controls.launchFailed"));
      }
      throw error;
    }
  },
);

export const fetchCheckpointResultExecute = createAsyncThunk(
  "checkpointBlock/fetchCheckpointResultExecute",
  async (checkpointExecute: CheckpointExecute) => {
    return apiControl.checkpointResultExecute(checkpointExecute);
  },
);

export const fetchCheckpointExecuteSoutirage = createAsyncThunk(
  "checkpointBlock/fetchCheckpointExecuteSoutirage",
  async (arg: [number, UploadFile]) => {
    return apiControl.checkpointExecuteSoutirage(arg[0], arg[1]);
  },
);

export const updateCheckpointResult = createAsyncThunk(
  "checkpointBlock/updateCheckpointResult",
  async (result: CheckpointResult) => {
    return apiControl.updateCheckpointResult(result);
  },
);

export const updateCheckpointBlockResultComment = createAsyncThunk(
  "checkpointBlock/updateCheckpointBlockResultComment",
  async (checkpointBlock: CheckpointBlock) => {
    return apiControl.updateCheckpointBlockResultComment(checkpointBlock);
  },
);

const processMercureMessage = (state: any, action: any): void => {
  let found = false;
  state.data.forEach((checkpointBlock: CheckpointBlock) => {
    checkpointBlock.checkpoints.forEach((checkpoint) => {
      if (checkpoint.result) {
        if (checkpoint.result.id === action.payload.id) {
          checkpoint.result = action.payload;
          found = true;
        }
      }
    });
  });
  if (!found) {
    // checkpoint not found in state, we store it to process it later,
    // when we will get backend response to fetchCheckpointsExecuteAll endpoint
    state.unprocessedMercureMessages.push(action);
  }
};

const checkpointsBlockSlice = createSlice({
  name: "CheckpointBlock",
  initialState,
  reducers: {
    updateCheckpointResultState: processMercureMessage,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCheckpointsBlock.fulfilled, (state, action) => {
        state.data = action.payload;
      })
      .addCase(fetchCheckpointsExecuteAll.fulfilled, (state, action) => {
        state.data = action.payload;

        // We have all checkpoint list now, process Mercure messages
        state.unprocessedMercureMessages.forEach((mercurePaylod) => {
          processMercureMessage(state, mercurePaylod);
        });
      })
      .addCase(fetchCheckpointExecuteSoutirage.fulfilled, (state, action) => {
        updateStateCheckpointResult(state, action.payload);
      })
      .addCase(fetchCheckpointResultExecute.fulfilled, (state, action) => {
        updateStateCheckpointResult(state, action.payload);
      })
      .addCase(updateCheckpointResult.fulfilled, (state, action) => {
        updateStateCheckpointResult(state, action.payload);
      })
      .addCase(updateCheckpointBlockResultComment.fulfilled, (state, action) => {
        state.data = state.data.map((checkpointBlock) => {
          if (checkpointBlock.id === action.payload.id) {
            return action.payload;
          }
          return checkpointBlock;
        });
      });
  },
});

export const {updateCheckpointResultState} = checkpointsBlockSlice.actions;

export default checkpointsBlockSlice.reducer;
